import React, {  useCallback, useState, useEffect, useMemo, useRef } from "react";
import debounce from "lodash.debounce";
import "../styles/ProductSearch.css"; 
import { apiRequest } from "../utils/api";
import { generateJan } from "../utils/jan"; 
import { usePurchaseItems } from "../context/PurchaseItemsContext";
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../components/AlertModal';

const ProductSearch = ({ selectedStore, supportId, setSupportId, setSearchTerm, searchTerm, searchResults, setSearchResults }) => {
  const { fetchItems, purchaseOrderId } = usePurchaseItems();
  const [currentPage, setCurrentPage] = useState(1); // 現在のページ
  const [totalPages, setTotalPages] = useState(1); // 総ページ数

  const [summaryitem, setSummaryItem] = useState(null);
  const [variationItems, setVariationItems] = useState([]);

  const searchResultsRef = useRef(null);

  const navigate = useNavigate();// ページ移動

  // JAN 入力用
  const [isJanModalOpen, setJanModalOpen] = useState(false);
  const [janInput, setJanInput] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [supportItem, setSupportItem] = useState(null);
  const [supportEditValues, setSupportEditValues] = useState({});
  const [adjustmentsSell, setAdjustmentsSell] = useState([]);
  const [adjustmentsBuy, setAdjustmentsBuy] = useState([]);
  const { showAlert } = useAlert();
  

  
  
  const fetchAdjustments = async (id) => {
    try {
      const resbuy = await apiRequest(`inventory/inventory_adjustments/?inventory_id=${id}&reason=仕入れ&limit=10`);
      const ressell = await apiRequest(`inventory/inventory_adjustments/?inventory_id=${id}&reason=販売&limit=10`);
      setAdjustmentsBuy(resbuy.results || []);
      setAdjustmentsSell(ressell.results || []);
    } catch (err) {
      console.error("在庫履歴取得失敗:", err);
    }
  };

  const fetchSupportItem = async (id) => {
    try {
      const data = await apiRequest(`inventory/inventory/${id}/`);
      setSupportItem(data);
    } catch (error) {
      console.error("サポート商品再取得エラー:", error);
    }
  };

  useEffect(() => {
    if (supportId) {
      fetchSupportItem(supportId);
      fetchAdjustments(supportId);
      fetchSalesPurchaseSummary(supportId);
      searchResultsRef.current?.scrollTo({ top: 0 });
      
    }
  }, [supportId]); 

  // バリエーション商品取得
  useEffect(() => {
    if (!supportItem?.etc) return;
  
    apiRequest(`inventory/related_versions/${supportItem.id}/`, 'GET')
      .then(setVariationItems);
  }, [supportItem]);
  
  // バリエーションラベル抽出  
  const extractVariationLabel = (etc) => {
    if (!etc) return "";
    const match = etc.match(/^Ver\d{5}(.*)$/); // 先頭Ver00001の後ろ全部を取る
    const label = match?.[1]?.trim();
    return label || ""; // 空文字なら何も表示しない
  };
  const handSupportId = async (id) => {
    setSupportId(id)    
    }

  
  useEffect(() => {
    if (supportItem && supportItem.store_inventory_set) {
      const initValues = {};
      supportItem.store_inventory_set.forEach((inv) => {
        initValues[inv.id] = {
          buy_price: inv.buy_price,
          buy_specialprice: inv.buy_specialprice,
          buy_top_switch: inv.buy_top_switch,
          buy_specialprice_switch: inv.buy_specialprice_switch,
          buy_specialprice_shoponly: inv.buy_specialprice_shoponly,
        };
      });
      setSupportEditValues(initValues);
    }
  }, [supportItem]);


  // 最新データ取得
  const fetchUpdatedInventory = async (inventoryId) => {
    try {
        // 更新された商品データを取得
        const updatedProduct = await apiRequest(`inventory/inventory/${inventoryId}/`);

        // 検索結果の一覧を更新
        setSearchResults((prevResults) =>
            prevResults.map((product) =>
                product.id === inventoryId ? updatedProduct : product
            )
        );
    } catch (error) {
        console.error("最新の商品データの取得に失敗しました:", error);
    }
  };

  // 集計データ取得
  const fetchSalesPurchaseSummary = async (id) => {
      try {
        const query = `store_id=${selectedStore}&inventory_id=${id}`;  
        const res = await apiRequest(`inventory/sales/summary/?${query}`);
        const item = res.results?.[0] || {};
    
        setSummaryItem({
          sales_summary: item.sales_summary || {},
          purchase_summary: item.purchase_summary || {},
          single_sales_summary: item.single_sales_summary || {},
          set_sales_summary: item.set_sales_summary || {},
        });
    
        console.log("✅ summary:", item);
      } catch (error) {
        console.error("販売・仕入集計の取得に失敗:", error);
      }
    }


  // 検索処理（ページネーション対応）
  const handleSearch = useCallback(async (page = 1) => {
    if (!searchTerm.trim()) return;
    try {
      const response = await apiRequest(`inventory/inventory/?q=${encodeURIComponent(searchTerm)}&page=${page}`);

      setSearchResults(response.results);
      setCurrentPage(page);
      setTotalPages(Math.ceil(response.count / 20));
    } catch (error) {
      console.error("検索エラー:", error);
    }
  }, [searchTerm, setSearchResults]);


  // 🔍 **検索処理（リアルタイム検索）**
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.trim()) {
        handleSearch(1);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, handleSearch]); // ✅ `handleSearch` を追加

  // ✖ **検索ボックスクリア**
  const clearSearch = () => {
    setSearchTerm("");
    setSearchResults([]); // 検索結果もリセット
  };


  const handleAddItem = async (product) => {
    if (!purchaseOrderId) {
      showAlert("伝票IDがありません。伝票を保存してから追加してください。");
      return;
    }
    if (!product.jan) {
      setSelectedProduct(product);
      setJanModalOpen(true);
      return;
    }
    try {
      // 選択された店舗の `store_inventory` を取得
      const selectedStoreInventory = product.store_inventory_set?.find(
        (storeInventory) => storeInventory.store.toString() === selectedStore
      );

      // `store_inventory` から価格を取得（選択された店舗）
      const price = selectedStoreInventory
        ? (selectedStoreInventory.buy_specialprice_switch
          ? selectedStoreInventory.buy_specialprice
          : selectedStoreInventory.buy_price)
        : product.buy_price;
      
      const newItem = {
        purchase_order_id: Number(purchaseOrderId),
        item_id: product.id,
        quantity: 1,
        unit_price: price || 0,
        subtotal: price || 0,
        condition_id: 1,
        box_condition: "",
        accessories_id: null
      };
  
      // 買取明細に追加
      await apiRequest(`inventory/purchase_orders/${purchaseOrderId}/items/`, "POST", newItem);
  
      // `fetchItems` を呼び出して最新の明細を取得
      fetchItems();
    } catch (error) {
      console.error("Error adding item:", error);
      showAlert("商品追加に失敗しました。");
    }
  };

  const handleJanSubmit = async () => {
    if (!janInput || (janInput.length !== 13 && janInput.length !== 18)) {
      showAlert("JANコードは13桁または18桁である必要があります");
      return;
    }

    try {
      await apiRequest(`inventory/inventory/${selectedProduct.id}/`, "PATCH", { jan: janInput });
      // モーダルを閉じる & 入力欄をクリア
      setJanInput("");
      setJanModalOpen(false);
      handleAddItem({ ...selectedProduct, jan: janInput });
      
      // 最新のデータを取得してリストを更新（遷移なしで即時反映）
      fetchUpdatedInventory(selectedProduct.id);
    
    } catch (error) {
      console.error("JAN 更新エラー:", error);
      showAlert("JANの更新に失敗しました。");
    }
  };
  
  
  // 買取更新
  const handleSupportInventoryChange = useCallback(async (storeInventoryId, updates, version) => {
    if (!storeInventoryId) return;
    const endpoint = `inventory/storeinventory/${storeInventoryId}/`;

    const payload = {
      ...updates,
      version_buy: version ?? 1,
    };

    try {
      await apiRequest(endpoint, "PATCH", payload);
      console.log("✅ 更新成功:", updates);

      // 正常更新後に最新取得
      fetchSupportItem(supportItem.id);

    } catch (error) {
      // 🔥 バージョンエラーなど
      if (error?.response?.status === 409) {
        showAlert("この在庫データは他で更新されました。最新の情報を再取得します。");
        fetchSupportItem(supportItem.id);
      } else {
        console.error("❌ 更新失敗:", error);
      }
    }
  }, [supportItem, showAlert]);  // supportItem が変更されたときに再定義される
  // 入力終了まで待つ
  const debouncedInventoryChange = useMemo(() =>
    debounce((id, updates, version) => {
      handleSupportInventoryChange(id, updates, version);
    }, 1500), [handleSupportInventoryChange]
  );

  useEffect(() => {
    return () => {
      debouncedInventoryChange.cancel(); // cleanup
    };
  }, [debouncedInventoryChange]);

  // フォーカス時に全選択する関数
  const handleFocus = (e) => {
    e.target.select();  // フォーカス時に全選択
  };

  const reloadsupport = () =>{
    fetchSupportItem(supportId)
  };

 
  return (
    <div className="product-search-container">
      <h2 className="section-title">商品検索</h2>

      {/*  検索バー（✖ボタン付き） */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="商品名/JAN/SKUを入力"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && <button className="clear-btn" onClick={clearSearch}>✖</button>}
      </div>

      {/* 検索結果表示 */}
      <div className="search-results" ref={searchResultsRef}>
        {searchResults.length > 0 ? (
          searchResults.map((product) => {
            const imageUrl = `/media/product_images/${product.sku}.jpg`;
            const selectedStoreInventory = product.store_inventory_set?.find(
              (storeInventory) => storeInventory.store.toString() === selectedStore
            );
            const price = selectedStoreInventory
              ? (selectedStoreInventory.buy_specialprice_switch
                ? selectedStoreInventory.buy_specialprice
                : selectedStoreInventory.buy_price)
              : product.buy_price;

            return (
              <div key={product.id} className="search-item">
                <div className="product-search-imgbox"><img src={imageUrl} alt={product.item_name} className="product-search-image" /></div>
                <div className="product-info">
                  <div onClick={() => navigate(`/inventory_detail/${product.id}/${selectedStore}`)}>
                  <p className="product-name">{product.item_name}</p>
                  <p className="product-barcode">JAN: {product.jan || "未登録"}</p>
                  {selectedStore ? (
                    selectedStoreInventory ? (
                      <p className="price">{selectedStoreInventory?.buy_specialprice_switch ? "特別" : "買取"}: {price != null ? `${price.toLocaleString()} 円` : "価格未設定"} </p>
                    ) : (
                      <p className="no-stock">⚠ この店舗には登録がありません</p>
                    )
                  ) : (
                    <p className="select-store">店舗を選択してください</p>
                  )}
                  </div>
                  <button className="add-btn" onClick={() => handleAddItem(product)}>➕ 追加</button>
                </div>
              </div>
            );
          })
        ) : supportId ? (
              supportItem ? (
                <div className="supportItem-container">
                  <p className="supportItem-mode">🔍 サポートモード表示中（ID: {supportId}）<label onClick={reloadsupport} className="reloadButton">🔄</label></p>

                  <img
                    src={`/media/product_images/${supportItem.sku}.jpg`}
                    alt={supportItem.item_name}
                    className="supportItem-image"
                  />

                  <p className="supportItem-name">{supportItem.item_name}</p>
                  <p className="supportItem-barcode">
                    メーカー: {supportItem.manufacturer || "未登録"}　/　JAN: {supportItem.jan || "未登録"}
                  </p>
                    {selectedStore ? (
                      supportItem.store_inventory_set
                        ?.filter(inv => inv.store.toString() === selectedStore)
                        .map((inventory, index) => (
                          <div key={inventory.id} className="supportItem-block">
                          <div className="supportItem-line">  
                            <label className="supportItem-label">
                              買取:
                              <input
                                type="number"
                                className="supportItem-input"
                                min={-9999999}
                                value={supportEditValues[inventory.id]?.buy_price ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setSupportEditValues((prev) => ({
                                    ...prev,
                                    [inventory.id]: {
                                      ...prev[inventory.id],
                                      buy_price: value,
                                    },
                                  }));
                                  debouncedInventoryChange(inventory.id, { buy_price: value }, inventory.version_buy);
                                }}
                                onFocus={handleFocus}
                              />
                            </label>

                            <div className="supportItem-switchGroup">
                              <input
                                type="checkbox"
                                id={`topswitch${inventory.id}`}
                                checked={
                                  supportEditValues[inventory.id]?.buy_top_switch ??
                                  inventory.buy_top_switch ??
                                  false
                                }
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSupportEditValues((prev) => ({
                                    ...prev,
                                    [inventory.id]: {
                                      ...prev[inventory.id],
                                      buy_top_switch: checked,
                                    },
                                  }));
                                  handleSupportInventoryChange(inventory.id, { buy_top_switch: checked }, inventory.version_buy);
                                }}
                              />
                              <label htmlFor={`topswitch${inventory.id}`} className="supportItem-checkbox checkbox1">TOP</label>
                              <p className="supportItem-meta">　更新日時: {inventory.update_date}</p>
                            </div>
                          </div>
                          <div className="supportItem-line">
                            <label className="supportItem-label">
                              特別:
                              <input
                                type="number"
                                className="supportItem-input"
                                value={supportEditValues[inventory.id]?.buy_specialprice ?? ""}
                                onChange={(e) => {
                                  const value = Number(e.target.value);
                                  setSupportEditValues((prev) => ({
                                    ...prev,
                                    [inventory.id]: {
                                      ...prev[inventory.id],
                                      buy_specialprice: value,
                                    },
                                  }));
                                  debouncedInventoryChange(inventory.id, { buy_specialprice: value }, inventory.version_buy);
                                }}
                                onFocus={handleFocus}
                              />
                            </label>
                            <div className="supportItem-switchGroup">
                              <input
                                type="checkbox"
                                id={`buy_specialprice_switch${inventory.id}`}
                                checked={
                                  supportEditValues[inventory.id]?.buy_specialprice_switch ??
                                  inventory.buy_specialprice_switch ??
                                  false
                                }
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSupportEditValues((prev) => ({
                                    ...prev,
                                    [inventory.id]: {
                                      ...prev[inventory.id],
                                      buy_specialprice_switch: checked,
                                    },
                                  }));
                                  handleSupportInventoryChange(inventory.id, { buy_specialprice_switch: checked }, inventory.version_buy);
                                }}
                              />
                              <label htmlFor={`buy_specialprice_switch${inventory.id}`} className="supportItem-checkbox checkbox2">特別</label>
                            </div>

                            <div className="supportItem-switchGroup">
                              <input
                                type="checkbox"
                                id={`buy_specialprice_shoponly${inventory.id}`}
                                checked={
                                  supportEditValues[inventory.id]?.buy_specialprice_shoponly ??
                                  inventory.buy_specialprice_shoponly ??
                                  false
                                }
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSupportEditValues((prev) => ({
                                    ...prev,
                                    [inventory.id]: {
                                      ...prev[inventory.id],
                                      buy_specialprice_shoponly: checked,
                                    },
                                  }));
                                  handleSupportInventoryChange(inventory.id, { buy_specialprice_shoponly: checked }, inventory.version_buy);
                                }}
                              />
                              <label htmlFor={`buy_specialprice_shoponly${inventory.id}`} className="supportItem-checkbox checkbox3">店限</label>
                            </div> 
                          </div>                            
                            <p className="supportItem-meta">在庫数: {inventory.stock} / 通常販売: {inventory.sell_price} / セット価格: {inventory.sell_set_price}</p>
                            <div className="detail_buttonbox">
                              <a href={`https://www.amazon.co.jp/s?k=${supportItem.jan}`} target="_blank" rel="noopener noreferrer">
                                <button className="detail_amozon_button">AM</button>
                              </a>
                              <a href={`https://slist.amiami.jp/top/search/list?s_keywords=${supportItem.jan}`} target="_blank" rel="noopener noreferrer">  
                                <button className="detail_amiami_button_s">AS</button>
                              </a>
                              <a href={`https://slist.amiami.jp/top/search/list9?s_keywords=${supportItem.jan}&submit=%E6%A4%9C%E7%B4%A2&pagemax=20&s_buy_flg=1`} target="_blank" rel="noopener noreferrer">
                                <button className="detail_amiami_button_b">AB</button>
                              </a>
                              <a
                                href={`https://www.suruga-ya.jp/search?category=&search_word=${
                                  [inventory.id]?.competitor_productid
                                    ?? inventory.competitorstore_set?.find((inv) => inv.store_name === "駿河屋")?.competitor_productid
                                    ?? (
                                      !supportItem.jan || supportItem.jan === ""
                                        ? supportItem.item_name
                                        : supportItem.jan.length === 18
                                          ? supportItem.jan.slice(-9)
                                          : supportItem.jan.startsWith("20137")
                                            ? supportItem.item_name
                                            : supportItem.jan
                                    )
                                }&searchbox=1`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <button className="detail_surugaya_button_s">SS</button>
                              </a> 
                              <a
                                href={`https://www.suruga-ya.jp/kaitori/search_buy?category=&search_word=${
                                  [inventory.id]?.competitor_productid
                                    ?? inventory.competitorstore_set?.find((inv) => inv.store_name === "駿河屋")?.competitor_productid
                                    ?? (
                                      !supportItem.jan || supportItem.jan === ""
                                        ? supportItem.item_name
                                        : supportItem.jan.length === 18
                                          ? supportItem.jan.slice(-9)
                                          : supportItem.jan.startsWith("20137")
                                            ? supportItem.item_name
                                            : supportItem.jan
                                    )
                                }&searchbox=1`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <button className="detail_surugaya_button_b">SB</button>
                              </a>                
                            </div>
                          </div>
                        ))
                    ) : (
                      <p className="select-store">店舗を選択してください</p>
                    )}
                    {summaryitem && (
                        <div className="summaryitem-container-ps">
                          <div className="summaryitem-header">
                          <span>各集計 </span>
                            <div className="summaryitem-header-number">1W</div>
                            <div className="summaryitem-header-number">1M</div>
                            <div className="summaryitem-header-number">3M</div>
                            <div className="summaryitem-header-number">6M</div>
                            <div className="summaryitem-header-number">12M</div>             
                          </div>
                          <div className="SalesItemCard-row">
                            <span>販売合計:</span>
                            {["1week", "1month", "3month", "6month", "12month"].map(
                              (key) => (
                                <div key={key} className="SalesItemCard-box">
                                  {summaryitem.sales_summary?.[key] ?? 0}
                                </div>
                              )
                            )}
                          </div>
                          <div className="SalesItemCard-row">
                            <span>単品販売:</span>
                            {["1week", "1month", "3month", "6month", "12month"].map(
                              (key) => (
                                <div key={key} className="SalesItemCard-box">
                                  {summaryitem.single_sales_summary?.[key] ?? 0}
                                </div>
                              )
                            )}
                          </div>
                          <div className="SalesItemCard-row">
                            <span>セット売:</span>
                            {["1week", "1month", "3month", "6month", "12month"].map(
                              (key) => (
                                <div key={key} className="SalesItemCard-box">
                                  {summaryitem.set_sales_summary?.[key] ?? 0}
                                </div>
                              )
                            )}
                          </div>
                          <div className="SalesItemCard-row">
                            <span>仕入合計:</span>
                            {["1week", "1month", "3month", "6month", "12month"].map(
                              (key) => (
                                <div key={key} className="SalesItemCard-box">
                                  {summaryitem.purchase_summary?.[key] ?? 0}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                      <>
                        {variationItems.length > 0 && (
                            <div className="variation-section-ps">
                              同一商品のバージョン違い
                              <ul>
                                {variationItems.map((ver) => (
                                  <li key={ver.id} onClick={() => handSupportId(ver.id)} className="variation-item">
                                    <img src={`/media/product_images/${ver.sku}.jpg?${Date.now()}`} alt=""
                                      onError={(e) => e.target.src = "/media/system_images/noimage.jpg"} />
                                    <div className="info">
                                      {extractVariationLabel(ver.etc) && `（${extractVariationLabel(ver.etc)}）`}
                                      <div className="name">ID: {ver.id}</div>
                                      <div className="meta">在庫: {ver.store_inventory_set?.find(inv => String(inv.store) === String(selectedStore))?.stock ?? 0}</div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                        )}
                      </>           
                </div>
              ) : (
                <p className="loading">🔄 サポートデータを読み込み中...</p>
              )
          
        ) : (
             <div className="FreeArea"><img
                  src={`https://www.pasomaru.co.jp/apps/image/marine_big01.png`}
                  alt="マリン船長"
                  className="supportarea-image"
                /></div>

        )}
      </div>

      {/* JANコード入力モーダル */}
      {isJanModalOpen && (
        <>
          <div className="jan-overlay" onClick={() => setJanModalOpen(false)}></div>
          <div className="jan-modal">
            <h3>JANコードを入力</h3>
            <input 
              type="text" 
              value={janInput} 
              onChange={(e) => setJanInput(e.target.value)}
              placeholder="13桁のJANを入力"
            />
            <button onClick={() => setJanInput(generateJan(selectedProduct.id))}>自動作成</button>
            <button onClick={handleJanSubmit}>登録</button>
            <button onClick={() => setJanModalOpen(false)}>キャンセル</button>
          </div>
        </>
      )}
      {/* ページネーション */}
      <div className="pagination-controls">
        <button 
          disabled={currentPage === 1} 
          onClick={() => handleSearch(currentPage - 1)}
        >
          前のページ
        </button>

        <span>ページ {currentPage} / {totalPages}</span>

        <button 
          disabled={currentPage >= totalPages} 
          onClick={() => handleSearch(currentPage + 1)}
        >
          次のページ
        </button>
      </div>
    </div>
  );
};

export default ProductSearch;
